import type { WorkspaceTab, WorkspaceTabDescriptor } from 'src/models/workspaceTabs';
import type { WorkspaceTabsState } from 'src/stores/workspaceTabs/state';

import { defineStore } from 'pinia';
import { createWorkspaceTabsState } from 'src/stores/workspaceTabs/state';

const createDefaultState = (): WorkspaceTabsState => createWorkspaceTabsState();

export const useWorkspaceTabsStore = defineStore('workspaceTabs', {
	actions: {
		activate(id: string): void {
			const tab = this.tabs.find((candidate) => candidate.id === id);
			if (tab !== undefined) {
				this.activeTabId = id;
				this.privateTrackModuleActivation(tab);
			}
		},
		claimDetailHandover(encodedAggregateId: string): boolean {
			if (!this.pendingDetailHandovers.includes(encodedAggregateId)) {
				return false;
			}
			this.pendingDetailHandovers = this.pendingDetailHandovers.filter(
				(candidate) => candidate !== encodedAggregateId
			);
			return true;
		},
		close(id: string): null | string {
			const index = this.tabs.findIndex((tab) => tab.id === id);
			const closedTab = this.tabs[index];
			if (closedTab === undefined || closedTab.closable === false) {
				return null;
			}

			const wasActive = this.activeTabId === id;
			const openerTabId = closedTab.openerTabId;
			// Kinder des geschlossenen Tabs erben dessen Opener (Kette Kanban → Ticket → Termin:
			// Ticket zuerst geschlossen ⇒ Termin springt später trotzdem ins Kanban zurück). Zyklen
			// sind ausgeschlossen: ein Opener referenziert immer einen früher erzeugten Tab, und das
			// Vererben erhält diese Ordnung.
			const remainingTabs = this.tabs
				.filter((tab) => tab.id !== id)
				.map((tab) => (tab.openerTabId === id ? { ...tab, openerTabId } : tab));
			this.tabs = remainingTabs;
			this.privateDropStaleOwner();

			if (!wasActive) {
				return null;
			}

			// Rücksprung zum Opener (ExtJS-Parität): der Tab, aus dem heraus geöffnet wurde, gewinnt.
			// Nur wenn er nicht mehr existiert, greift der bisherige Nachbar-Fallback.
			const opener = remainingTabs.find((tab) => tab.id === openerTabId) ?? null;
			const next = opener ?? remainingTabs[index] ?? remainingTabs[index - 1] ?? null;
			this.activeTabId = next === null ? null : next.id;
			if (next !== null) {
				this.privateTrackModuleActivation(next);
			}
			return next === null ? null : next.id;
		},
		closeAll(): void {
			this.tabs = this.tabs.filter((tab) => tab.closable === false);
			this.privateDropStaleOwner();
			const firstRemaining = this.tabs[0] ?? null;
			this.activeTabId = firstRemaining === null ? null : firstRemaining.id;
			if (firstRemaining !== null) {
				this.privateTrackModuleActivation(firstRemaining);
			}
		},
		closeOthers(id: string): void {
			if (!this.tabs.some((tab) => tab.id === id)) {
				return;
			}

			this.tabs = this.tabs.filter((tab) => tab.id === id || tab.closable === false);
			this.privateDropStaleOwner();
			this.activate(id);
		},
		hasDetailHandover(encodedAggregateId: string): boolean {
			return this.pendingDetailHandovers.includes(encodedAggregateId);
		},
		markDetailHandover(encodedAggregateId: string): void {
			if (this.pendingDetailHandovers.includes(encodedAggregateId)) {
				return;
			}
			this.pendingDetailHandovers = [...this.pendingDetailHandovers, encodedAggregateId];
		},
		openOrActivate(descriptor: WorkspaceTabDescriptor): void {
			const existing = this.tabs.find((tab) => tab.id === descriptor.id);
			if (existing !== undefined) {
				// Route-tragende Felder aus dem Deskriptor auffrischen (Deep-Link-Replay mit neuen
				// Params/Query trifft sonst einen eingefrorenen Tab); title/dirty bleiben erhalten,
				// closable/icon/titleKey/type stammen weiter aus dem bestehenden Tab.
				this.tabs = this.tabs.map((tab) =>
					tab.id === existing.id
						? {
								...tab,
								params: descriptor.params,
								query: descriptor.query,
								routeName: descriptor.routeName,
							}
						: tab
				);
				this.activeTabId = existing.id;
				this.privateTrackModuleActivation(existing);
				return;
			}

			// Opener = aktiver Tab im Moment der Erzeugung. Nur hier setzen — der Existing-Branch oben
			// läuft via router.afterEach bei jeder Navigation und würde den Opener sonst bei jedem
			// Tab-Wechsel überschreiben (MRU statt deterministischem Rücksprung).
			const newTab: WorkspaceTab = {
				...descriptor,
				dirty: false,
				iconColor: null,
				openerTabId: this.activeTabId,
				title: null,
			};
			this.tabs = [...this.tabs, newTab];
			this.activeTabId = newTab.id;
			this.privateTrackModuleActivation(newTab);
		},
		privateDropStaleOwner(): void {
			if (
				this.lastActiveModuleTabId !== null &&
				!this.tabs.some((tab) => tab.id === this.lastActiveModuleTabId)
			) {
				this.lastActiveModuleTabId = null;
			}
		},
		privateTrackModuleActivation(tab: WorkspaceTab): void {
			if (tab.type === 'module') {
				this.lastActiveModuleTabId = tab.id;
			}
		},
		reorder(fromIndex: number, toIndex: number): void {
			if (
				fromIndex < 0 ||
				toIndex < 0 ||
				fromIndex >= this.tabs.length ||
				toIndex >= this.tabs.length ||
				fromIndex === toIndex
			) {
				return;
			}
			const reordered = [...this.tabs];
			const [moved] = reordered.splice(fromIndex, 1);
			if (moved === undefined) {
				return;
			}
			reordered.splice(toIndex, 0, moved);
			this.tabs = reordered;
		},
		resetTabs(): void {
			this.activeTabId = null;
			this.lastActiveModuleTabId = null;
			this.tabs = [];
		},
		setDirty(id: string, dirty: boolean): void {
			this.tabs = this.tabs.map((tab) => (tab.id === id ? { ...tab, dirty } : tab));
		},
		setIconColor(id: string, iconColor: string): void {
			this.tabs = this.tabs.map((tab) => (tab.id === id ? { ...tab, iconColor } : tab));
		},
		setTitle(id: string, title: string): void {
			this.tabs = this.tabs.map((tab) => (tab.id === id ? { ...tab, title } : tab));
		},
		updateRoute(id: string, params: Record<string, string>, query: Record<string, string>): void {
			this.tabs = this.tabs.map((tab) => (tab.id === id ? { ...tab, params, query } : tab));
		},
	},
	getters: {
		activeTab(state): WorkspaceTab | null {
			return state.tabs.find((tab) => tab.id === state.activeTabId) ?? null;
		},
		detailOwnerModuleTabId(state): null | string {
			const active = state.tabs.find((tab) => tab.id === state.activeTabId);
			if (active !== undefined && active.type === 'module') {
				return active.id;
			}
			if (state.lastActiveModuleTabId !== null) {
				return state.lastActiveModuleTabId;
			}
			// Fallback: der aktive Tab ist ein Objekt-Tab und es gab noch nie einen aktiven Modul-Tab
			// (z. B. Deep-Link-Einstieg direkt auf ein Objekt) — der geseedete Startseiten-Tab ist im
			// Staging-Modus immer vorhanden, also den ersten Modul-Tab als Besitzer wählen statt null.
			const firstModuleTab = state.tabs.find((tab) => tab.type === 'module');
			return firstModuleTab?.id ?? null;
		},
	},
	state: createDefaultState,
});
